import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../config';
import './userDash.css';

const STATUS_LABELS = {
  ordered: 'Ordered',
  ready: 'Ready for Delivery',
  delivered: 'Delivered',
  refunded: 'Refunded',
  canceled: 'Canceled',
};

const UserDash = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reordering, setReordering] = useState(null);
  const [toast, setToast] = useState(null);           // { type: 'success'|'warning', text }
  const navigate = useNavigate();

  /* auto-dismiss toast after 4 s */
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const fetchOrders = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in to view your order history.');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/orderhistory/`, {
          headers: { Authorization: `Token ${token}` },
        });
        setOrders(res.data);
      } catch (err) {
        console.error('Failed to load order history:', err);
        setError('Could not load order history.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const handleReorder = async (order) => {
    const token = localStorage.getItem('authToken');
    if (!token) { navigate('/login'); return; }

    setReordering(order.order_id);
    let added = 0;
    const failed = [];

    for (const item of order.items) {
      try {
        await axios.post(
          `${BASE_URL}/api/add-to-cart/`,
          { slug: item.item_slug, quantity: item.quantity },
          { headers: { Authorization: `Token ${token}` } }
        );
        added++;
      } catch (err) {
        console.warn(`Could not add "${item.item_title}":`, err?.response?.data?.error);
        failed.push(item.item_title);
      }
    }

    setReordering(null);

    if (failed.length > 0 && added === 0) {
      setToast({ type: 'warning', text: `None of the items could be added. They may no longer be available.` });
    } else if (failed.length > 0) {
      setToast({ type: 'warning', text: `Added ${added} item(s). Could not add: ${failed.join(', ')}.` });
      navigate('/cart');
    } else {
      setToast({ type: 'success', text: 'All items added to cart!' });
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="oh-page">
        <h1 className="oh-title">My Orders</h1>
        <p className="oh-loading">Loading your orders…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="oh-page">
        <h1 className="oh-title">My Orders</h1>
        <p className="oh-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="oh-page">
      <h1 className="oh-title">My Orders</h1>

      {/* ── Toast notification ── */}
      {toast && (
        <div className={`oh-toast oh-toast--${toast.type}`}>
          <span>{toast.text}</span>
          <button className="oh-toast-close" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="oh-empty">
          <p>You haven't placed any orders yet.</p>
          <button className="oh-shop-btn" onClick={() => navigate('/products')}>
            Start Shopping
          </button>
        </div>
      ) : (
        <div className="oh-list">
          {orders.map((order) => (
            <div key={order.order_id} className="oh-card">
              {/* ── Header row ── */}
              <div className="oh-card-header">
                <div className="oh-card-meta">
                  <span className="oh-order-id">
                    Order {order.order_number} from {new Date(order.created_at).toLocaleDateString('de-CH')}
                  </span>
                </div>
                <span className={`oh-status oh-status--${order.order_status}`}>
                  {STATUS_LABELS[order.order_status] || order.order_status}
                </span>
              </div>

              {/* ── Delivery info ── */}
              {order.delivery_date && (
                <div className="oh-delivery">
                  Delivery: {new Date(order.delivery_date).toLocaleDateString('en-CH', {
                    year: 'numeric', month: 'short', day: 'numeric',
                  })}
                  {order.delivery_time && ` · ${order.delivery_time}`}
                </div>
              )}

              {/* ── Items table ── */}
              <table className="oh-items">
                <thead>
                  <tr>
                    <th className="oh-th-product">Product</th>
                    <th className="oh-th-qty">Qty</th>
                    <th className="oh-th-price">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.item_title}</td>
                      <td className="oh-center">{item.quantity}</td>
                      <td className="oh-right">CHF {parseFloat(item.price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ── Footer row ── */}
              <div className="oh-card-footer">
                <div className="oh-total">
                  Total: <strong>CHF {parseFloat(order.total).toFixed(2)}</strong>
                </div>
                <button
                  className="oh-reorder-btn"
                  onClick={() => handleReorder(order)}
                  disabled={reordering === order.order_id}
                >
                  {reordering === order.order_id ? 'Adding…' : '🔄 Reorder'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserDash;
